<script lang="ts" setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import RBush from "rbush";
import center from "@turf/center";
import { injectMap } from "@/utils/MapUtils";
import { type ComputedMarker } from "@/shared/types/zod";
import { type ProgressMode } from "@/shared/types/types";
import { getIcon } from "@/shared/Icons";
import { addRoundRectPath } from "@/shared/renderers/base/RenderUtils";
import { getCachedMarkerIcon } from "@/shared/renderers/renderMarkerIcon";
import { useStore } from "@/store/Store";
import { useProgressStore } from "@/store/ProgressStore";
import { storeToRefs } from "pinia";
import { EventBus } from "@/utils/EventBus";
import { setMarkerCanvasHitTest } from "@/draw/markerHitTest";
import { getDrawOverrides } from "@/draw/drawOverrides";
import { progressValues } from "@/utils/markerProgress";
import { MarkerAnimationController } from "./marker-canvas/markerAnimation";
import { getPreparedMarkerCollisionBox, prepareMarkerPlacements, publishMarkerPlacements, type PreparedMarkerPlacement } from "./marker-canvas/markerPlacement";
import { MarkerLabelPlacementCache } from "./marker-canvas/markerLabelPlacement";
import { getMarkerLabelPlacements, resolveMarkerLabelLayout } from "./marker-canvas/markerLabelLayout";
import { type CollisionBox } from "./marker-canvas/collision";
import { getShapeTextPlacements, resolveShapeTextLayout } from "./marker-canvas/shapeTextLayout";

const $map = injectMap();
const store = useStore();
const progressStore = useProgressStore();
const { isDarkMap, currentLanguageSuffix, computedShapes, trackFeature, trackFeatureLegs, categoryFilter } = storeToRefs(store);

const props = defineProps<{
	features: ComputedMarker[];
	progress: number;
	progressMode?: ProgressMode;
	actualTrackLength: number;
	editMode?: boolean;
	selectedFeatureIds?: string[];
}>();

const markerVisibility = computed((): number => {
	if (props.editMode) return 1;
	if (props.progressMode == "animation") return props.progress;
	else if (props.progressMode == "height" || props.progressMode == "animationVideo") return props.progress;
	return 1;
});

const textVisibility = computed((): number => {
	if (props.editMode) return 1;
	if (props.progressMode == "animation") return 0;
	else if (props.progressMode == "height" || props.progressMode == "animationVideo") return props.progress;
	return 1;
});

const showMarkerLabels = computed(() => store.map?.markerLabels !== false);
const popupMarkerId = ref<string | undefined>(undefined);

// Sort by z-index first, then descending latitude (north first = drawn first).
const sortedFeatures = computed(() =>
	[...props.features].sort((a, b) =>
		(a.properties.__zIndex ?? 0) - (b.properties.__zIndex ?? 0)
		|| b.geometry.coordinates[1] - a.geometry.coordinates[1]
	)
);

interface ShapeTextPoint {
	id: string;
	coords: [number, number];
	text: string;
	textAnchor: "center" | "center-fixed" | "left" | "right" | "top" | "bottom";
	textRotation: number;
	textZoom: number;
	isTextOnly: boolean;
	excluded: boolean;
}

const shapeTextPoints = computed((): ShapeTextPoint[] => {
	const suffix = currentLanguageSuffix.value;
	const points: ShapeTextPoint[] = [];
	for (const shape of computedShapes.value) {
		if (shape.properties.__excluded) continue;
		if (shape.geometry.type === "LineString") continue;
		if (shape.properties.shapeType === "image") continue;

		const text = (suffix && shape.properties[("text" + suffix) as keyof typeof shape.properties] as string)
			|| shape.properties.text || "";
		if (!text) continue;

		let coords: [number, number];
		if (shape.geometry.type === "Polygon" && shape.geometry.coordinates.length > 0) {
			const c = center(shape as any);
			coords = c.geometry.coordinates as [number, number];
		} else {
			coords = shape.geometry.coordinates as [number, number];
		}

		points.push({
			id: String(shape.id),
			coords,
			text,
			textAnchor: shape.properties.textAnchor ?? "center",
			textRotation: shape.properties.textRotation ?? 0,
			textZoom: shape.properties.textZoom ?? 16,
			isTextOnly: shape.geometry.type === "Point" && shape.properties.shapeType === "",
			excluded: !!shape.properties.__excluded,
		});
	}
	return points;
});

const canvasRef = ref<HTMLCanvasElement>();
let ctx: CanvasRenderingContext2D | null = null;
let resizeObserver: ResizeObserver | null = null;
let redrawRafId: number | null = null;

// Base marker scale relative to rendered icon canvas size.
const CULL_MARGIN = 150;
const DOT_RADIUS = 2.5;
const LABEL_FONT_SIZE = 11;
const LABEL_FONT_SIZE_GENERATED = 14;
const LABEL_FONT = "Open Sans, sans-serif";
const LABEL_FONT_REGULAR = "Open Sans, sans-serif";
const LABEL_HALO_WIDTH = 1.5;
const LABEL_MAX_WIDTH = 150;
const LABEL_LINE_HEIGHT = 1.2;
const COLLISION_PADDING = 0;

const SHAPE_TEXT_FONT_SIZE = 11;
const SHAPE_TEXT_HALO_WIDTH = 1.5;
const SHAPE_TEXT_COLOR = "#444";

const SELECTION_COLOR = "#fbb03b";
const SELECTION_LINE_WIDTH = 3;
const TRACK_TIP_RADIUS = 8;
const TRACK_TIP_BORDER_WIDTH = 2;

const markerAnimations = new MarkerAnimationController();
const labelPlacements = new MarkerLabelPlacementCache();
const shapeTextAnimations = new MarkerAnimationController();
const shapeTextPlacements = new MarkerLabelPlacementCache();
let collisionTree = new RBush<CollisionBox>();

let debugHitboxes = false;

function onKeyDown(e: KeyboardEvent) {
	if (!import.meta.env.DEV) return;
	// Use `code` so layout/Option-modified characters on mac still work.
	const isToggle = (e.altKey && e.code === "KeyH") || (e.ctrlKey && e.shiftKey && e.code === "KeyH");
	if (isToggle) {
		debugHitboxes = !debugHitboxes;
		scheduleRedraw();
		e.preventDefault();
	}
}

function getMarkerImage(marker: ComputedMarker): HTMLCanvasElement | undefined {
	const icon = getIcon(marker.properties.icon);
	const color = icon.fixedColor ?? marker.properties.color;
	return getCachedMarkerIcon(icon, color, {
		variation: marker.properties.__markerVariation,
		text: marker.properties.text,
	}, scheduleRedraw);
}

function updateCanvasSize() {
	const canvas = canvasRef.value;
	if (!canvas) return;
	const parent = canvas.parentElement;
	if (!parent) return;

	const dpr = window.devicePixelRatio || 1;
	const width = parent.clientWidth;
	const height = parent.clientHeight;

	canvas.width = width * dpr;
	canvas.height = height * dpr;
	canvas.style.width = `${width}px`;
	canvas.style.height = `${height}px`;

	ctx = canvas.getContext("2d");
	if (ctx) {
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	}
	redraw();
}

function getMarkerLabel(marker: ComputedMarker): string {
	const suffix = currentLanguageSuffix.value;
	if (suffix) {
		const localized = marker.properties["label" + suffix as keyof typeof marker.properties] as string | undefined;
		if (localized) return localized;
	}
	return marker.properties.label ?? "";
}

function getAnchorOffset(anchor: string, imgWidth: number, imgHeight: number, offsetX = 0, offsetY = 0): [number, number] {
	let x = anchor.includes("left") ? imgWidth / 2 : anchor.includes("right") ? -imgWidth / 2 : 0;
	let y = anchor.includes("top") ? imgHeight / 2 : anchor.includes("bottom") ? -imgHeight / 2 : 0;
	x += offsetX;
	y += offsetY;
	if (anchor.includes("-")) { x /= 1.414; y /= 1.414; }
	return [x, y];
}

function wrapText(text: string, maxWidth: number): string[] {
	const words = text.split(" ");
	const lines: string[] = [];
	let line = "";

	for (const word of words) {
		const test = line ? line + " " + word : word;
		if (ctx!.measureText(test).width > maxWidth && line) {
			lines.push(line);
			line = word;
		} else {
			line = test;
		}
	}
	if (line) lines.push(line);
	return lines;
}

function scheduleRedraw() {
	if (redrawRafId != null) return;
	redrawRafId = requestAnimationFrame(() => {
		redrawRafId = null;
		redraw();
	});
}

function getTrackTipColor(progress: number): string {
	const legs = trackFeatureLegs.value;
	if (!legs.length) return trackFeature.value.properties?.color ?? "#000";

	if (progress <= 0) {
		return legs[0].properties?.color ?? trackFeature.value.properties?.color ?? "#000";
	}

	const activeLeg = legs.find((leg) => progress <= (leg.properties?.endProgress ?? 1));
	return activeLeg?.properties?.color ?? legs[legs.length - 1].properties?.color ?? trackFeature.value.properties?.color ?? "#000";
}

function drawTrackTip() {
	if (!ctx || props.actualTrackLength <= 0) return;

	const currentProgress = props.progress ?? 0;
	if (currentProgress >= 1) return;
	const dpr = window.devicePixelRatio || 1;

	const tipCoordinates = store.trackIndex.sampleAtDistance(props.actualTrackLength * Math.max(currentProgress, 0.0000001));
	const tipPoint = $map.project(tipCoordinates);
	const tipColor = getTrackTipColor(currentProgress);
	const outerRadius = TRACK_TIP_RADIUS + TRACK_TIP_BORDER_WIDTH;

	ctx.save();
	ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
	ctx.shadowOffsetX = 1 * dpr;
	ctx.shadowOffsetY = 1 * dpr;
	ctx.shadowBlur = 3 * dpr;
	ctx.beginPath();
	ctx.arc(tipPoint.x, tipPoint.y, outerRadius, 0, Math.PI * 2);
	ctx.fillStyle = "#fff";
	ctx.fill();
	ctx.restore();

	ctx.save();
	ctx.beginPath();
	ctx.arc(tipPoint.x, tipPoint.y, TRACK_TIP_RADIUS, 0, Math.PI * 2);
	ctx.fillStyle = tipColor;
	ctx.fill();
	ctx.restore();
}

function redraw() {
	if (redrawRafId != null) { cancelAnimationFrame(redrawRafId); redrawRafId = null; }

	const canvas = canvasRef.value;
	if (!canvas || !ctx) return;

	const dpr = window.devicePixelRatio || 1;
	const width = canvas.width / dpr;
	const height = canvas.height / dpr;
	ctx.clearRect(0, 0, width, height);

	const zoom = $map.getZoom();
	const drawOverrides = getDrawOverrides();
	const mVis = markerVisibility.value;
	const tVis = textVisibility.value;
	const isEditMode = props.editMode;
	const selectedIds = isEditMode ? new Set(props.selectedFeatureIds) : null;
	const now = performance.now();
	const { prepared, hasAnimatingMarkers } = prepareMarkerPlacements({
		features: sortedFeatures.value,
		zoom,
		width,
		height,
		isEditMode,
		ignoreZoom: categoryFilter.value.length > 0,
		markerVisibility: mVis,
		textVisibility: tVis,
		now,
		cullMargin: CULL_MARGIN,
		getImage: getMarkerImage,
		getProjectedPoint(marker) {
			const coords = drawOverrides.get(marker.id) ?? marker.geometry.coordinates;
			return $map.project(coords as [number, number]);
		},
		getAnimation(marker, context) {
			return markerAnimations.update(marker, {
				...context,
				ignoreZoom: context.ignoreZoom || marker.id === popupMarkerId.value,
			});
		},
		getPlacementIndex(markerId) {
			return labelPlacements.get(markerId);
		},
	});
	let animating = hasAnimatingMarkers;
	publishMarkerPlacements(prepared);

	let drawOrder = 2000;
	// ── Phase A2: Build collision tree ──────────────────────────────────
	const tree = new RBush<CollisionBox>();
	for (const p of prepared) {
		// Reserve the icon box while within zoom (even before the animation reveals the marker)
		// and while fading out, so labels don't reflow as upcoming markers appear.
		if (!p.animation.withinZoom && p.animation.iconScale <= 0) continue;
		tree.insert(getPreparedMarkerCollisionBox(p, drawOrder++));
	}

	// ── Phase B1: Draw dots (below markers) ─────────────────────────────
	for (const p of prepared) {
		if (p.animation.iconScale > 0) continue;
		const excluded = !!p.marker.properties.__excluded;
		if (excluded) ctx.globalAlpha = 0.4;
		ctx.beginPath();
		ctx.arc(p.screenX, p.screenY, DOT_RADIUS, 0, Math.PI * 2);
		ctx.fillStyle = p.marker.properties.color ?? "#666";
		ctx.fill();
		ctx.strokeStyle = "#fff";
		ctx.lineWidth = 1;
		ctx.stroke();

		// Selection ring for dots
		if (selectedIds?.has(p.marker.id)) {
			ctx.beginPath();
			ctx.arc(p.screenX, p.screenY, DOT_RADIUS + 3, 0, Math.PI * 2);
			ctx.strokeStyle = SELECTION_COLOR;
			ctx.lineWidth = SELECTION_LINE_WIDTH;
			ctx.stroke();
		}

		if (excluded) ctx.globalAlpha = 1;
	}

	// ── Phase B1.5: Draw track tip ──────────────────────────────────────
	drawTrackTip();

	// ── Phase B2: Draw markers (above dots) ─────────────────────────────
	for (const p of prepared) {
		const t = p.animation.iconScale;
		if (t <= 0) continue;

		const excluded = !!p.marker.properties.__excluded;
		if (excluded) ctx.globalAlpha = 0.4;
		const drawCx = p.screenX + (p.cx - p.screenX) * t;
		const drawCy = p.screenY + (p.cy - p.screenY) * t;
		const dotW = DOT_RADIUS * 2;
		const dotH = DOT_RADIUS * 2;
		const drawW = dotW + (p.imgWidth - dotW) * t;
		const drawH = dotH + (p.imgHeight - dotH) * t;
		ctx.drawImage(p.image, drawCx - drawW / 2, drawCy - drawH / 2, drawW, drawH);
		if (excluded) ctx.globalAlpha = 1;

		// Selection ring
		if (selectedIds?.has(p.marker.id) && t >= 1) {
			const icon = getIcon(p.marker.properties.icon);
			ctx.strokeStyle = SELECTION_COLOR;
			ctx.lineWidth = SELECTION_LINE_WIDTH;
			if (icon.renderer === "sign") {
				const half = Math.max(drawW, drawH) / 2 + 1;
				ctx.beginPath();
				addRoundRectPath(ctx, drawCx - half, drawCy - half, half * 2, half * 2, 3);
				ctx.stroke();
			} else {
				const radius = Math.max(drawW, drawH) / 2 + 1;
				ctx.beginPath();
				ctx.arc(drawCx, drawCy, radius, 0, Math.PI * 2);
				ctx.stroke();
			}
		}

	}

	// ── Phase B3: Draw labels ───────────────────────────────────────────
	const dark = isDarkMap.value;
	const haloColor = dark ? "#000" : "#fff";
	const shouldDrawMarkerLabels = showMarkerLabels.value;
	let labelDrawOrder = 3000;

	function drawLabel(p: PreparedMarkerPlacement) {
		const { animation, marker } = p;
		// Whether the label is revealed (drawn) this frame.
		const drawEligible = shouldDrawMarkerLabels && animation.labelEligible;
		// Whether the label should claim a collision slot even if it isn't drawn yet (e.g. a
		// marker the route animation hasn't reached). This keeps visible labels clear of spots
		// upcoming labels will occupy. Markers hidden by zoom (withinZoom=false) reserve nothing.
		const participates = shouldDrawMarkerLabels && (animation.labelEligible || (animation.withinZoom && tVis > 0));
		const label = getMarkerLabel(marker);

		if (!label) {
			markerAnimations.updateLabel(marker.id, false, now);
			p.placementIndex = undefined;
			labelPlacements.forget(marker.id);
			return;
		}

		const { cx: x, cy: y, imgWidth, imgHeight } = p;

		const fontSize = marker.properties.__generated ? LABEL_FONT_SIZE_GENERATED : LABEL_FONT_SIZE;
		let placements = getMarkerLabelPlacements(imgWidth, imgHeight, marker.properties.anchor ?? "center");
		if (marker.properties.__generated) {
			const forcedDirection = marker.properties.__forceLabelAnchorRight ? "left" : "right";
			placements = placements.filter((p) => p.direction === forcedDirection);
		}

		ctx!.font = `bold ${fontSize}px ${LABEL_FONT}`;
		const lines = wrapText(label, LABEL_MAX_WIDTH);
		const lineH = fontSize * LABEL_LINE_HEIGHT;
		const blockHeight = lines.length * lineH;

		let blockWidth = 0;
		for (const line of lines) {
			blockWidth = Math.max(blockWidth, ctx!.measureText(line).width);
		}

		// Generated markers (start/goal) always show their label and still block other labels,
		// unless markerLabels is disabled — in that case they follow the same rules as regular markers.
		const isGeneratedWithLabels = marker.properties.__generated && shouldDrawMarkerLabels;
		let layout = (participates || isGeneratedWithLabels)
			? resolveMarkerLabelLayout({
				x, y, blockWidth, blockHeight, placements,
				cachedIndex: p.placementIndex,
				markerId: marker.id,
				drawOrder: labelDrawOrder++,
				collisionPadding: COLLISION_PADDING,
				tree,
				forcePlace: isGeneratedWithLabels,
			})
			: undefined;

		// Drive the fade by whether the label is actually drawn this frame, not by mere
		// reservation \u2014 reserved-but-unrevealed labels stay at opacity 0.
		const drawn = (drawEligible || isGeneratedWithLabels) && !!layout;
		const labelAnim = markerAnimations.updateLabel(marker.id, !!drawn, now);
		if (labelAnim.isAnimating) animating = true;

		if (labelAnim.opacity === 0) {
			if (layout) {
				// Reserved a slot for an upcoming marker: remember it so it stays stable, but
				// don't draw anything yet.
				p.placementIndex = layout.placementIndex;
				labelPlacements.remember(marker.id, layout.placementIndex);
			} else {
				p.placementIndex = undefined;
				labelPlacements.forget(marker.id);
			}
			return;
		}

		// Fading out (hidden by zoom or overlap): redraw at the last placement without collisions.
		if (!layout && p.placementIndex != null) {
			layout = resolveMarkerLabelLayout({
				x, y, blockWidth, blockHeight, placements,
				cachedIndex: p.placementIndex,
				markerId: marker.id,
				drawOrder: labelDrawOrder++,
				collisionPadding: COLLISION_PADDING,
				tree,
				skipCollision: true,
			});
		}
		if (!layout) return;

		ctx!.textAlign = layout.textAlign;
		ctx!.textBaseline = "top";
		p.placementIndex = layout.placementIndex;
		labelPlacements.remember(marker.id, layout.placementIndex);

		const textColor = marker.properties.__generated
			? (dark ? "#eee" : "#333")
			: (dark ? "#eee" : (marker.properties.textColor ?? "#333"));

		ctx!.globalAlpha = labelAnim.opacity * (marker.properties.__excluded ? 0.4 : 1);
		for (let i = 0; i < lines.length; i++) {
			const lineY = layout.startY + i * lineH;
			ctx!.strokeStyle = haloColor;
			ctx!.lineWidth = LABEL_HALO_WIDTH * 2;
			ctx!.lineJoin = "round";
			ctx!.strokeText(lines[i], layout.textX, lineY);
			ctx!.fillStyle = textColor;
			ctx!.fillText(lines[i], layout.textX, lineY);
		}
		ctx!.globalAlpha = 1;
	}

	labelPlacements.drawInPlacementOrder(prepared, drawLabel);

	// ── Phase B4: Draw shape texts ──────────────────────────────────────
	if (drawShapeTexts(tree, zoom, width, height, dark, haloColor, selectedIds, drawOverrides, now)) {
		animating = true;
	}

	// ── Phase B5: Add dot hit boxes (hit testing only) ─────────────────
	let dotDrawOrder = 1000;
	for (const p of prepared) {
		if (p.animation.iconScale > 0) continue;
		const dotHitRadius = DOT_RADIUS + 4;
		tree.insert({
			minX: p.screenX - dotHitRadius,
			minY: p.screenY - dotHitRadius,
			maxX: p.screenX + dotHitRadius,
			maxY: p.screenY + dotHitRadius,
			markerId: p.marker.id,
			drawOrder: dotDrawOrder++,
		});
	}

	// ── Debug hitboxes ──────────────────────────────────────────────────
	if (debugHitboxes) {
		for (const box of tree.all()) {
			ctx.strokeStyle = box.isLabel ? "rgba(0,200,0,0.6)" : "rgba(255,0,0,0.6)";
			ctx.lineWidth = 1;
			ctx.strokeRect(box.minX, box.minY, box.maxX - box.minX, box.maxY - box.minY);
		}
	}

	// ── Finalize ────────────────────────────────────────────────────────
	collisionTree = tree;
	if (animating) scheduleRedraw();

	const activeIds = new Set(props.features.map(f => f.id));
	markerAnimations.cleanup(activeIds, now);
	labelPlacements.cleanup(activeIds, now);

	const activeShapeTextIds = new Set(shapeTextPoints.value.map(st => st.id));
	shapeTextAnimations.cleanup(activeShapeTextIds, now);
	shapeTextPlacements.cleanup(activeShapeTextIds, now);
}

function drawShapeTexts(
	tree: RBush<CollisionBox>,
	zoom: number,
	width: number,
	height: number,
	dark: boolean,
	haloColor: string,
	selectedIds: Set<string> | null,
	drawOverrides: ReadonlyMap<string, [number, number]>,
	now: number,
): boolean {
	if (!ctx) return false;

	let animating = false;
	const fontSize = SHAPE_TEXT_FONT_SIZE;
	const lineH = fontSize * LABEL_LINE_HEIGHT;

	for (const st of shapeTextPoints.value) {
		const eligible = zoom >= st.textZoom;

		const coords = drawOverrides.get(st.id) ?? st.coords;
		const projected = $map.project(coords);
		const sx = projected.x;
		const sy = projected.y;

		const margin = LABEL_MAX_WIDTH;
		if (sx < -margin || sy < -margin || sx > width + margin || sy > height + margin) continue;

		ctx.font = `${fontSize}px ${LABEL_FONT_REGULAR}`;

		const lines = wrapText(st.text, LABEL_MAX_WIDTH);
		const blockHeight = lines.length * lineH;
		let blockWidth = 0;
		for (const line of lines) {
			blockWidth = Math.max(blockWidth, ctx.measureText(line).width);
		}

		const placements = getShapeTextPlacements(st.textAnchor);
		const cachedIndex = shapeTextPlacements.get(st.id);
		const isSelected = st.isTextOnly && !!selectedIds?.has(st.id);

		// Only compete for space (and insert into the collision tree) while eligible.
		let layout = eligible
			? resolveShapeTextLayout({
				sx, sy, blockWidth, blockHeight,
				textRotation: st.textRotation,
				placements,
				markerId: st.id,
				tree,
				cachedIndex,
			})
			: undefined;
		const placedNow = !!layout;

		const anim = shapeTextAnimations.updateLabel(st.id, placedNow, now);
		if (anim.isAnimating) animating = true;

		if (anim.opacity === 0) {
			shapeTextPlacements.forget(st.id);
			continue;
		}

		// Fading out (hidden by zoom or overlap): redraw at the last placement without collisions.
		if (!layout && cachedIndex != null) {
			layout = resolveShapeTextLayout({
				sx, sy, blockWidth, blockHeight,
				textRotation: st.textRotation,
				placements,
				markerId: st.id,
				tree,
				cachedIndex,
				skipCollision: true,
			});
		}
		if (!layout) continue;

		shapeTextPlacements.remember(st.id, layout.placementIndex);

		const textColor = dark ? "#eee" : SHAPE_TEXT_COLOR;

		ctx.textAlign = layout.textAlign;
		ctx.textBaseline = "top";
		ctx.globalAlpha = anim.opacity;

		if (st.textRotation) {
			ctx.save();
			ctx.translate(sx, sy);
			ctx.rotate((st.textRotation * Math.PI) / 180);
			ctx.translate(-sx, -sy);
		}

		for (let i = 0; i < lines.length; i++) {
			const lineY = layout.startY + i * lineH;
			ctx.strokeStyle = haloColor;
			ctx.lineWidth = SHAPE_TEXT_HALO_WIDTH * 2;
			ctx.lineJoin = "round";
			ctx.strokeText(lines[i], layout.textX, lineY);
			ctx.fillStyle = textColor;
			ctx.fillText(lines[i], layout.textX, lineY);
		}

		if (isSelected) {
			const selectionPad = 4;
			ctx.strokeStyle = SELECTION_COLOR;
			ctx.lineWidth = 2;
			ctx.beginPath();
			addRoundRectPath(
				ctx,
				layout.startX - selectionPad,
				layout.startY - selectionPad,
				blockWidth + selectionPad * 2,
				blockHeight + selectionPad * 2,
				4,
			);
			ctx.stroke();
		}

		if (st.textRotation) {
			ctx.restore();
		}

		ctx.globalAlpha = 1;

		// Draw a dot at the feature center (unless text is centered on it, or hidden by zoom).
		if (layout.placedCenter || !eligible) continue;
		const dotColor = dark ? "#eee" : SHAPE_TEXT_COLOR;
		const dotRadius = 1.5;
		const dotOuterRadius = dotRadius + SHAPE_TEXT_HALO_WIDTH * 2;
		const dotBox: CollisionBox = {
			minX: sx - dotOuterRadius,
			minY: sy - dotOuterRadius,
			maxX: sx + dotOuterRadius,
			maxY: sy + dotOuterRadius,
			drawOrder: 0,
		};
		if (!placedNow) {
			const overlapsLabel = tree.search(dotBox).some((box) => box.isLabel);
			if (overlapsLabel) continue;
		}
		ctx.save();
		ctx.globalAlpha = anim.opacity;
		ctx.globalCompositeOperation = "destination-over";
		ctx.beginPath();
		ctx.arc(sx, sy, dotRadius, 0, Math.PI * 2);
		ctx.fillStyle = dotColor;
		ctx.fill();
		ctx.strokeStyle = haloColor;
		ctx.lineWidth = SHAPE_TEXT_HALO_WIDTH * 2;
		ctx.lineJoin = "round";
		ctx.stroke();
		ctx.restore();
	}

	return animating;
}

function hitTestXY(x: number, y: number): CollisionBox | undefined {
	const hits = collisionTree.search({ minX: x, minY: y, maxX: x, maxY: y });
	let topmost: CollisionBox | undefined;
	for (const hit of hits) {
		if (!hit.markerId || hit.drawOrder == null) continue;
		if (!topmost || hit.drawOrder > topmost.drawOrder!) {
			topmost = hit;
		}
	}
	return topmost;
}

function hitTest(e: MouseEvent): CollisionBox | undefined {
	const rect = $map.getCanvas().getBoundingClientRect();
	return hitTestXY(e.clientX - rect.left, e.clientY - rect.top);
}

function onClick(e: MouseEvent) {
	if (!store._editMode) {
		const hit = hitTest(e);
		if (hit) {
			EventBus.emit("showPopup", { id: hit.markerId! });
		}
	}
}

// for player
function onMouseMove(e: MouseEvent) {
	const hit = hitTest(e);
	if (hit?.markerId) {
		const hoveredMarker = props.features.find((feature) => feature.id === hit.markerId);
		const hoveredProgresses = progressValues(hoveredMarker?.properties.__progress);
		if (hoveredMarker?.properties.icon !== "live" && hoveredProgresses.length > 0) {
			progressStore.handleMouseMoveFeature(hit.markerId, hoveredProgresses);
		}
	}
	$map.getCanvas().style.cursor = hit && !hit.isLabel ? "pointer" : "";
}

function showPopup({ id }: { id: string | number | undefined }) {
	popupMarkerId.value = id == undefined ? undefined : String(id);
	redraw();
}

function closePopup() {
	popupMarkerId.value = undefined;
	redraw();
}

onMounted(() => {
	updateCanvasSize();

	resizeObserver = new ResizeObserver(updateCanvasSize);
	if (canvasRef.value?.parentElement) {
		resizeObserver.observe(canvasRef.value.parentElement);
	}

	$map.on("move", redraw);
	$map.on("draw.render", redraw);
	EventBus.on("showPopup", showPopup);
	EventBus.on("closePopup", closePopup);
	if (import.meta.env.DEV) {
		window.addEventListener("keydown", onKeyDown);
	}

	setMarkerCanvasHitTest((x, y) => hitTestXY(x, y)?.markerId);

	const container = $map.getContainer();
	container.addEventListener("click", onClick);
	container.addEventListener("mousemove", onMouseMove);
});

onBeforeUnmount(() => {
	$map.off("move", redraw);
	$map.off("draw.render", redraw);
	EventBus.off("showPopup", showPopup);
	EventBus.off("closePopup", closePopup);
	if (import.meta.env.DEV) {
		window.removeEventListener("keydown", onKeyDown);
	}
	if (redrawRafId != null) { cancelAnimationFrame(redrawRafId); redrawRafId = null; }
	resizeObserver?.disconnect();
	setMarkerCanvasHitTest(null);
	const container = $map.getContainer();
	container.removeEventListener("click", onClick);
	container.removeEventListener("mousemove", onMouseMove);
});

watch(() => props.features, () => {
	scheduleRedraw();
}, { deep: true });

watch(() => props.progress, () => {
	scheduleRedraw();
});

watch(() => props.progressMode, () => {
	scheduleRedraw();
});

watch(() => props.actualTrackLength, () => {
	scheduleRedraw();
});

watch(() => store.map?.markerLabels, () => {
	scheduleRedraw();
});

watch(categoryFilter, () => {
	scheduleRedraw();
}, { deep: true });

watch(() => props.selectedFeatureIds, () => {
	scheduleRedraw();
});

watch(computedShapes, () => {
	scheduleRedraw();
}, { deep: true });

watch(trackFeatureLegs, () => {
	scheduleRedraw();
}, { deep: true });
</script>

<template>
	<canvas ref="canvasRef" class="marker-canvas" />
</template>

<style scoped>
.marker-canvas {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	z-index: 10;
}
</style>
